import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Archive, BarChart3, Plus, RefreshCw, Search, Sparkles } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  knowledgeBaseApi,
  type KbDocument,
  type KbDocumentStatus,
} from "@/api/knowledgeBase";
import { KbStatusBadge } from "@/components/knowledgeBase/KbStatusBadge";
import { KbDocumentEditDialog } from "@/components/knowledgeBase/KbDocumentEditDialog";
import { KbDocumentUploadDialog } from "@/components/knowledgeBase/KbDocumentUploadDialog";
import { KbAiIntakeDialog } from "@/components/knowledgeBase/KbAiIntakeDialog";
import { Modal } from "@/components/ui/Modal";
import { useDateFormatter } from "@/utils/datetime";

// Knowledge Base v1.0 PR 4 — admin document library. Top-of-page filters
// (category / tag / status / search / includeRetired), single + bulk upload,
// re-index, and per-row actions (edit, replace file, retire/restore).

const QUERY_KEY = ["kb", "admin", "documents"] as const;
const STATUS_OPTIONS: KbDocumentStatus[] = [
  "Draft",
  "Processing",
  "Indexed",
  "Failed",
  "Retired",
];

export function KnowledgeBaseDocumentsAdminPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { dateTime, date } = useDateFormatter();

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<KbDocumentStatus | "">("");
  const [includeRetired, setIncludeRetired] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [aiIntakeOpen, setAiIntakeOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KbDocument | null>(null);
  const [registryRetireOpen, setRegistryRetireOpen] = useState(false);

  const docsQ = useQuery({
    queryKey: [...QUERY_KEY, categoryFilter, tagFilter, statusFilter, includeRetired],
    queryFn: () =>
      knowledgeBaseApi.listDocuments({
        categoryCode: categoryFilter || null,
        tagCode: tagFilter || null,
        status: statusFilter || null,
        includeRetired,
      }),
    enabled: canManage,
  });

  const categoriesQ = useQuery({
    queryKey: ["kb", "categories"],
    queryFn: () => knowledgeBaseApi.listCategories(),
    enabled: canManage,
  });

  const tagsQ = useQuery({
    queryKey: ["kb", "tags"],
    queryFn: () => knowledgeBaseApi.listTags(),
    enabled: canManage,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const retireMutation = useMutation({
    mutationFn: (id: string) => knowledgeBaseApi.retireDocument(id),
    onSuccess: () => {
      toast.success("Document retired.");
      refresh();
    },
    onError: (err) => toast.error(extractMessage(err, "Could not retire document.")),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => knowledgeBaseApi.restoreDocument(id),
    onSuccess: () => {
      toast.success("Document restored.");
      refresh();
    },
    onError: (err) => toast.error(extractMessage(err, "Could not restore document.")),
  });

  const reindexMutation = useMutation({
    mutationFn: () => knowledgeBaseApi.reindex(),
    onSuccess: (result) => {
      const status = result.indexerRun?.status ?? "queued";
      toast.success(`Re-index ${status}.`);
      refresh();
    },
    onError: (err) => toast.error(extractMessage(err, "Could not trigger re-index.")),
  });

  const retireByRegistryMutation = useMutation({
    mutationFn: (abbreviations: string[]) =>
      knowledgeBaseApi.retireByRegistry(abbreviations),
    onSuccess: (result) => {
      const unmatched =
        result.unmatchedAbbreviations.length > 0
          ? ` (no match: ${result.unmatchedAbbreviations.join(", ")})`
          : "";
      toast.success(`Retired ${result.retiredCount} document(s)${unmatched}.`);
      setRegistryRetireOpen(false);
      refresh();
    },
    onError: (err) =>
      toast.error(extractMessage(err, "Could not retire by Registry abbreviation.")),
  });

  const filtered = useMemo(() => {
    const rows = docsQ.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.title.toLowerCase().includes(needle));
  }, [docsQ.data, search]);

  const categoryByCode = useMemo(() => {
    const m = new Map<string, string>();
    (categoriesQ.data ?? []).forEach((c) => m.set(c.code, c.name));
    return m;
  }, [categoriesQ.data]);

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage the knowledge base.
      </div>
    );
  }

  const columns: Column<KbDocument>[] = [
    {
      key: "title",
      header: "Title",
      render: (d) => (
        <div className="min-w-0">
          <div className="font-semibold text-onyx truncate">{d.title}</div>
          {d.description && (
            <div className="text-caption text-gray-mid truncate max-w-md">
              {d.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (d) => (
        <span className="text-small text-onyx">
          {categoryByCode.get(d.primaryCategoryCode) ?? d.primaryCategoryCode}
        </span>
      ),
    },
    {
      key: "tags",
      header: "Tags",
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {d.tagCodes.length === 0 ? "—" : d.tagCodes.join(", ")}
        </span>
      ),
    },
    {
      key: "version",
      header: "Version",
      render: (d) => (
        <span className="font-mono text-caption text-gray-mid">
          {d.documentVersion ?? "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (d) => <KbStatusBadge status={d.status} />,
    },
    {
      key: "index",
      header: "Indexed",
      render: (d) =>
        d.indexError ? (
          <span
            className="text-caption text-status-error truncate inline-block max-w-[12rem]"
            title={d.indexError}
          >
            {d.indexError}
          </span>
        ) : d.indexedAt ? (
          <span className="text-caption text-gray-mid">
            {dateTime(d.indexedAt)}
          </span>
        ) : (
          <span className="text-caption text-gray-mid">—</span>
        ),
    },
    {
      key: "updated",
      header: "Updated",
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {date(d.updatedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (d) => (
        <div className="flex items-center gap-space-1 justify-end">
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => setEditTarget(d)}
          >
            Edit
          </Button>
          {d.status === "Retired" ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => restoreMutation.mutate(d.id)}
              disabled={restoreMutation.isPending}
            >
              Restore
            </Button>
          ) : (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => {
                if (window.confirm(`Retire '${d.title}'?`)) {
                  retireMutation.mutate(d.id);
                }
              }}
              disabled={retireMutation.isPending}
            >
              Retire
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Documents"
        description="Upload, edit, retire, and re-index documents in the curated knowledge base."
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              type="button"
              variant="tertiary"
              onClick={() => setRegistryRetireOpen(true)}
            >
              <Archive size={16} aria-hidden /> Retire by Registry
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => reindexMutation.mutate()}
              loading={reindexMutation.isPending}
            >
              <RefreshCw size={16} aria-hidden /> Re-index
            </Button>
            <Button
              type="button"
              variant="tertiary"
              onClick={() =>
                navigate("/admin/knowledge-base-classification-accuracy")
              }
            >
              <BarChart3 size={16} aria-hidden /> Accuracy
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAiIntakeOpen(true)}
            >
              <Sparkles size={16} aria-hidden /> AI Upload
            </Button>
            <Button type="button" onClick={() => setUploadOpen(true)}>
              <Plus size={16} aria-hidden /> Upload
            </Button>
          </div>
        }
      />

      <div className="flex flex-col md:flex-row md:items-end gap-space-3 mb-space-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search
              size={16}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title"
              className="pl-space-5"
              aria-label="Search documents by title"
            />
          </div>
        </div>
        <Select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label="Filter by category"
          className="md:w-48"
        >
          <option value="">All categories</option>
          {(categoriesQ.data ?? []).map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          aria-label="Filter by tag"
          className="md:w-44"
        >
          <option value="">Any tag</option>
          {(tagsQ.data ?? []).map((t) => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </Select>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as KbDocumentStatus | "")}
          aria-label="Filter by status"
          className="md:w-40"
        >
          <option value="">Any status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)}
          />
          Show retired
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={docsQ.isLoading}
        error={docsQ.isError ? (docsQ.error as Error).message : null}
        emptyMessage="No documents match your filters. Click Upload to add one."
        rowKey={(d) => d.id}
      />

      {uploadOpen && (
        <KbDocumentUploadDialog
          categories={categoriesQ.data ?? []}
          tags={tagsQ.data ?? []}
          onClose={() => setUploadOpen(false)}
          onSaved={() => {
            setUploadOpen(false);
            refresh();
          }}
        />
      )}

      {aiIntakeOpen && (
        <KbAiIntakeDialog
          categories={categoriesQ.data ?? []}
          tags={tagsQ.data ?? []}
          onClose={() => setAiIntakeOpen(false)}
          onSaved={() => {
            setAiIntakeOpen(false);
            refresh();
          }}
        />
      )}

      {editTarget && (
        <KbDocumentEditDialog
          document={editTarget}
          categories={categoriesQ.data ?? []}
          tags={tagsQ.data ?? []}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            refresh();
          }}
        />
      )}

      {registryRetireOpen && (
        <KbRetireByRegistryDialog
          onClose={() => setRegistryRetireOpen(false)}
          onSubmit={(abbreviations) => retireByRegistryMutation.mutate(abbreviations)}
          submitting={retireByRegistryMutation.isPending}
        />
      )}
    </>
  );
}

// Knowledge Base v1.0 PR 5 — Registry-driven bulk retire dialog. Default
// pre-fill carries the v1.0 Master Program Document Registry RETIRED set
// (FCA, KEA, PSAR, BLA Exhibit E-2, STCF). Operators edit the textarea
// before submitting so future RETIRED items can be queued without a code
// change.
function KbRetireByRegistryDialog({
  onClose,
  onSubmit,
  submitting,
}: {
  onClose: () => void;
  onSubmit: (abbreviations: string[]) => void;
  submitting: boolean;
}) {
  const [text, setText] = useState("FCA\nKEA\nPSAR\nBLA Exhibit E-2\nSTCF");
  const abbreviations = text
    .split(/\r?\n|,/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Retire documents by Registry abbreviation"
      description="Marks every non-retired document whose Registry abbreviation matches one of the entries below. Retired documents stay in the library and index but are excluded from answers."
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(abbreviations)}
            loading={submitting}
            disabled={abbreviations.length === 0}
          >
            Retire {abbreviations.length || ""}
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="text-caption text-gray-mid">
          Registry abbreviations (one per line, or comma-separated)
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          aria-label="Registry abbreviations"
          className="mt-space-1 w-full rounded-md border border-border-light bg-white px-space-2 py-space-2 font-mono text-small focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </label>
    </Modal>
  );
}

function extractMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return err.errors?.[0] ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
